// src/components/utiles/api.js
// Config + fetchers + endpoints centralizados

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const TOKEN_KEYS = [
  process.env.REACT_APP_JWT_STORAGE_KEY,
  "token",
  "JWT_TOKEN",
].filter(Boolean);

export const getToken = () => {
  for (const k of TOKEN_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return "";
};

export const apiUrl = (path = "") => {
  if (!path) throw new Error("apiUrl(path) vacío/undefined");
  return path.startsWith("http")
    ? path
    : `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

function withAuth(init = {}) {
  const token = getToken();
  if (!token) throw new Error("No hay JWT en localStorage. Inicia sesión.");
  const headers = new Headers(init.headers || {});
  if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

/* ======================================================
   🔐 MANEJO GLOBAL DE AUTENTICACIÓN (AÑADIDO)
====================================================== */

function clearAuthStorage() {
  for (const k of TOKEN_KEYS) {
    localStorage.removeItem(k);
  }
}

function handleAuthError(status) {
  // 401 → token inválido / expirado / clave rotada
  if (status === 401) {
    console.warn("🔒 Token inválido o expirado. Forzando logout.");
    clearAuthStorage();

    if (!window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
  }
}

/* ======================================================
   Fetchers
====================================================== */

export async function fetchJson(path, init = {}) {
  const res = await fetch(
    apiUrl(path),
    withAuth({
      cache: "no-store",
      ...init,
      headers: {
        ...(init.headers || {}),
        ...(init.body &&
        !(init.headers instanceof Headers) &&
        !((init.headers || {})["Content-Type"])
          ? { "Content-Type": "application/json" }
          : {}),
      },
      credentials: "omit",
    })
  );

  if (res.status === 204 || res.status === 205) return {};

  if (!res.ok) {
    handleAuthError(res.status); // ✅ NUEVO
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${res.statusText} • ${text.slice(0, 200)}`);
  }

  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : {};
}

export async function fetchBlob(path, init = {}) {
  const res = await fetch(
    apiUrl(path),
    withAuth({ cache: "no-store", ...init, credentials: "omit" })
  );

  if (!res.ok) {
    handleAuthError(res.status); // ✅ NUEVO
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }

  return res.blob();
}

export const headersAuth = () => ({ Authorization: `Bearer ${getToken()}` });

/* ======================================================
   Endpoints
====================================================== */

export const API = {
  generar: "/api/remesas/generar",
  listar: "/api/remesas",
  detalle: (id) => `/api/remesas/${id}`,
  pain008: (id) => `/api/remesas/${id}/pain008`,
  eliminar: (id) => `/api/remesas/${id}`,
  setResultado: (reciboId, valor) =>
    `/api/recibos/${reciboId}/resultado?valor=${encodeURIComponent(valor)}`,
  bulkResultado: (remesaId) => `/api/remesas/${remesaId}/recibos/resultado`,
  bulkRemesable: (remesaId) => `/api/remesas/${remesaId}/recibos/remesable`,
  generarDevoluciones: "/api/remesas/parcial/devoluciones",
  listarDevoluciones: "/api/remesas/devoluciones",
  marcarDevolucion: (id, pagada) =>
    `/api/remesas/devoluciones/${id}/marcar${
      typeof pagada === "boolean" ? `?pagada=${pagada}` : ""
    }`,
  colegiadoHist: (codigo) => `/api/colegiados/${codigo}/historial`,
  colegiadoBasics: (codigo) => `/api/colegiados/${codigo}/basicos`,
  historialLogs: (codigo) => `/api/remesas/historial/logs/${codigo}`,
  buscarRecibos: "/api/recibos",
  bulkResultadoIds: "/api/recibos/bulk/resultado",
  bulkRemesableIds: "/api/recibos/bulk/remesable",
  setEstadoDevolucion: (id, valor) =>
    `/api/remesas/devoluciones/${id}/estado?valor=${valor}`,
  ventanillaCalcular: "/api/remesas/ventanilla/calcular",
  actualizarParcial: (id) => `/api/remesas/${id}/parcial/actualizar`,
};

export const API_MAND = {
  tmpList: "/api/mandatos/tmp/pendientes",
  tmpUpdate: (id) => `/api/mandatos/tmp/${id}`,
  tmpApprove: (id) => `/api/mandatos/tmp/${id}/aprobar`,
  tmpDelete: (id) => `/api/mandatos/tmp/${id}`,
  listByColegiado: (codigo) =>
    `/api/mandatos?colegiado=${encodeURIComponent(codigo)}`,
  defUpdate: (id) => `/api/mandatos/${id}`, // PATCH { mndtId, fechaFirma, activo }
};
